import React, { useState, useMemo } from 'react';
import {
    Box,
    Container,
    Paper,
    Button,
    Chip,
    Popper,
    Typography,
    Divider
} from '@mui/material';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { startOfMonth, endOfMonth, subMonths, format, startOfWeek, endOfWeek, subWeeks, startOfYear, endOfYear } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardCharts from './DashboardCharts';
import { useGetDashboardStatsQuery, useGetDashboardChartsQuery } from '../dashboard/services';
import OrderSummary from 'components/OrderSummary/OrderSummary';
import MainContainer from 'theme/MainConatiner';
import CustomSelect from 'components/Fileds/CustomSelect';
import { ordersFilterConfigs, paymentMethodOptions } from 'utils/constant';
import "./style.css";
import CustomTypography from 'components/Fileds/CustomTypography';

const Analytics = () => {
    // Define presets
    const presets = [
        {
            label: 'Current Week',
            range: {
                startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
                endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
            }
        },
        {
            label: 'Previous Week',
            range: {
                startDate: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
                endDate: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
            }
        },
        {
            label: 'Current Month',
            range: {
                startDate: startOfMonth(new Date()),
                endDate: endOfMonth(new Date())
            }
        },
        {
            label: 'Previous Month',
            range: {
                startDate: startOfMonth(subMonths(new Date(), 1)),
                endDate: endOfMonth(subMonths(new Date(), 1))
            }
        },
        {
            label: 'Current Year',
            range: {
                startDate: startOfYear(new Date()),
                endDate: endOfYear(new Date())
            }
        }
    ];

    // Default to Current Week
    const [selectionRange, setSelectionRange] = useState({
        startDate: presets[0].range.startDate,
        endDate: presets[0].range.endDate,
        key: 'selection',
        color: '#000'
    });

    const [pickerOpen, setPickerOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const [orderType, setOrderType] = useState('All Types');
    const [paymentMethod, setPaymentMethod] = useState('All Payments');

    // Prepare params for API
    const queryParams = {
        startDate: selectionRange.startDate ? format(selectionRange.startDate, 'yyyy-MM-dd') : '',
        endDate: selectionRange.endDate ? format(selectionRange.endDate, 'yyyy-MM-dd') : '',
        orderType: orderType === 'All Types' ? '' : orderType,
        paymentMethod: paymentMethod === 'All Payments' ? '' : paymentMethod
    };

    const { data: statsData, isFetching: isStatsFetching } = useGetDashboardStatsQuery(queryParams);
    const { data: chartsData, isFetching: isChartsFetching } = useGetDashboardChartsQuery(queryParams);

    // Map statsData to OrderSummary format
    const summaryList = useMemo(() => {
        const data = statsData?.data || statsData || {};
        return [
            { id: "1", title: "Total Orders", value: data.totalOrders || 0, highlight: true, },
            { id: "2", title: "Total Revenue", value: `$${(data.totalRevenue || 0).toLocaleString()}`, highlight: true, },
            { id: "3", title: "Total Customers", value: data.totalCustomers || 0, highlight: true, },
            { id: "4", title: "Table Booking", value: data.reservations || 0, highlight: true, },
            { id: "5", title: "Cancelled", value: data.cancelled || 0, highlight: true, }
        ];
    }, [statsData]);

    const monthStaticRanges = useMemo(() => presets.map(preset => ({
        label: preset.label,
        range: () => ({
            startDate: preset.range.startDate,
            endDate: preset.range.endDate,
        }),
        isSelected: range =>
            range.startDate?.getTime() === preset.range.startDate.getTime() &&
            range.endDate?.getTime() === preset.range.endDate.getTime(),
        color: '#3d91ff'
    })), [presets]);

    // Prepare Options for Selects
    const orderTypeOptions = useMemo(() => {
        const options = ordersFilterConfigs.map(config => ({
            label: config.label,
            value: config.id
        }));
        // Filter out 'All' if we want to handle it manually or rename it, 
        // usually 'All' id is 'All' in config.
        // We can just use them as is, but let's ensure 'All Types' matches.
        // Config has { id: 'All', label: 'All' }.
        // Our state default is 'All Types'. Let's map 'All' to 'All Types' or just use 'All'.
        // To keep consistent with previous state 'All Types', let's just use what's in config or normalize.
        // Actually, let's use the config directly.
        // If config has 'All', we select 'All'.
        // Update state initialization if needed.
        return options.map(o => o.label === 'All' ? { ...o, label: 'All Types', value: 'All Types' } : o);
    }, []);

    const paymentOptions = useMemo(() => {
        return [
            { label: 'All Payments', value: 'All Payments' },
            ...paymentMethodOptions
        ];
    }, []);


    let matchedPreset;
    const getChipLabel = (selectionRange, presets) => {
        const { startDate, endDate } = selectionRange;
        if (!startDate || !endDate) return 'Apply Date Filter';

        matchedPreset = presets.find(p =>
            p.range.startDate.getTime() === startDate.getTime() &&
            p.range.endDate.getTime() === endDate.getTime()
        );

        if (matchedPreset?.label.includes('Month')) {
            return (
                <Typography className='date-chip-label' variant='body2'>
                    Showing orders of <b>{format(startDate, 'MMMM yyyy')}</b>
                </Typography>
            )
        }

        return (
            <Typography variant='body2' className='date-chip-label'>
                Showing orders of <b>{format(startDate, 'dd MMM yyyy')} to {format(endDate, 'dd MMM yyyy')}</b>
            </Typography>
        )
    };

    const handleButtonClick = (e) => {
        setAnchorEl(e.currentTarget);
        setPickerOpen(open => !open);
    };

    const handleSelect = (ranges) => {
        setSelectionRange({ ...ranges.selection, color: '#000' });
        setPickerOpen(false);
    };

    return (
        <MainContainer>
            <Box className="pos-container" sx={{ flexGrow: 1 }}>
                <Container
                    maxWidth={false}
                    disableGutters
                    sx={{
                        px: { xs: 1, sm: 2, md: 0 },
                        py: 2,
                        maxWidth: "1400px",
                        mx: "auto",
                    }}
                >
                    <Box className='d-flex align-items-center justify-content-between'>
                        <Box className='d-flex' sx={{ flexDirection: 'column' }}>
                            <CustomTypography variant="h5" label="Dashboard" sx={{ fontSize: 20, fontWeight: 700, mb: 0.5 }} />
                            <CustomTypography
                                variant={"body2"}
                                label={"Monitor your restaurant's performance"}
                                sx={{ fontSize: '14px', color: '#737373', fontWeight: '500', mb: 2 }}
                            />
                        </Box>

                        {/* Filters Section */}
                        <Paper className='dashboard-border' sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderRadius: 2, boxShadow: 'none' }}>

                            {/* Date Picker Trigger */}
                            <Box sx={{ position: 'relative' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<CalendarMonthIcon />}
                                    onClick={handleButtonClick}
                                    sx={{
                                        textTransform: 'none',
                                        lineHeight: '1.5',
                                        borderColor: 'rgba(0, 0, 0, 0.23)',
                                        color: 'text.primary',
                                        minWidth: '200px',
                                        justifyContent: 'flex-start',
                                        height: '45px'
                                    }}
                                >
                                    {selectionRange?.startDate && selectionRange?.endDate
                                        ? `${format(selectionRange.startDate, 'MMM dd, yyyy')} - ${format(selectionRange.endDate, 'MMM dd, yyyy')}`
                                        : 'Select Date Range'
                                    }
                                </Button>

                                <Popper
                                    open={pickerOpen}
                                    anchorEl={anchorEl}
                                    placement="bottom-start"
                                    onClose={() => setPickerOpen(false)}
                                    modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                                    sx={{ zIndex: 1300 }}
                                >
                                    <Paper elevation={3}>
                                        <DateRangePicker
                                            locale={enUS}
                                            onChange={handleSelect}
                                            months={2}
                                            direction="horizontal"
                                            showDateDisplay={false}
                                            showPreview={false}
                                            staticRanges={monthStaticRanges}
                                            inputRanges={[]}
                                            initialFocusedRange={[0, 0]}
                                            moveRangeOnFirstSelection={false}
                                            ranges={[selectionRange.startDate ? selectionRange : {
                                                startDate: new Date(),
                                                endDate: new Date(),
                                                key: 'selection',
                                                color: '#000'
                                            }]}
                                        />
                                    </Paper>
                                </Popper>
                            </Box>

                            {/* Order Type Filter */}
                            <CustomSelect
                                value={orderType}
                                options={orderTypeOptions}
                                onChange={(e) => setOrderType(e.target.value)}
                                formWidth="200px"
                                placeholder="Order Type"
                            />

                            {/* Payment Method Filter */}
                            <CustomSelect
                                value={paymentMethod}
                                options={paymentOptions}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                formWidth="250px"
                                placeholder="Payment Method"
                            />

                        </Paper>
                    </Box>


                    {/* Summary Cards */}
                    <OrderSummary
                        orderSummary={summaryList}
                        customGridSize={{ xs: 6, sm: 6, md: 3, lg: 2.4 }}
                    />


                    <Divider sx={{ my: 3 }} />

                    {/* Charts */}
                    {/* Fix: use chartsData?.data if exists, else chartsData */}
                    <DashboardCharts data={chartsData?.data || chartsData} loading={isChartsFetching} />

                </Container>
            </Box>
        </MainContainer>
    );
};

export default Analytics;
